/**
 * Typed Hono RPC client for the Relevé backend (@releve/api).
 *
 * Usage:
 *   const res = await api.api.v1.inbox[":id"].$patch({ param: { id }, json: patch });
 *   if (!res.ok) throw new Error((await res.json()).error);
 *   return res.json();
 *
 * The Authorization header is resolved per request from the current Supabase
 * session, so token refresh stays automatic (autoRefreshToken: true in
 * `@/integrations/supabase/client`).
 *
 * The client's TYPE is `Client` from `@releve/api/client`: a declaration file
 * the API emits under its strict tsconfig (`npm run build:client-types`, run
 * by `typecheck`, CI and `npm run dev`). Typing `hc` here with the route
 * generics would re-instantiate them under web's non-strict settings, which
 * drops every validated `json` input. A TS2307 on that import means the
 * declaration file has not been built yet.
 *
 * See docs/BACKEND_API.md → "Frontend RPC client" and "Frontend RPC client types".
 */
import { hc } from "hono/client";
import type { Client } from "@releve/api/client";
import { VAT_DECLARED, type VatDeclaredRefusal } from "@releve/shared";
import { supabase } from "@/integrations/supabase/client";

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * Authorization header from the current Supabase session (empty when signed
 * out). Shared by the typed `hc` client and `authedPost` so they always
 * authenticate identically.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token
    ? { Authorization: `Bearer ${data.session.access_token}` }
    : {};
}

export const api = hc(BASE_URL, { headers: authHeaders }) as unknown as Client;

/**
 * Refresh the Supabase session first when its access token expires within
 * `minSeconds`. For a request whose server-side work outlives the response and
 * keeps using the caller's JWT (the crew-email dispatcher runs up to 270 s);
 * autoRefreshToken alone may hand over a token with less time than that left.
 * Best-effort: a failed refresh leaves the current session for the request.
 */
export async function ensureFreshSession(minSeconds: number): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const expiresAt = data.session?.expires_at;
  if (expiresAt === undefined || expiresAt - Date.now() / 1000 >= minSeconds) return;
  await supabase.auth.refreshSession();
}

/**
 * Error thrown by `unwrap`/`unwrapEnvelope` on a non-2xx response. Carries the
 * HTTP status (and Retry-After, when the server sent one) so callers can
 * branch on 429/401 structurally instead of string-matching the message. The
 * message text is unchanged from the plain-Error era — existing catch sites
 * and toast descriptions are unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Parsed delta-seconds from the Retry-After header, when present and numeric. */
  readonly retryAfterSeconds?: number;

  constructor(message: string, status: number, retryAfterSeconds?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Delta-seconds form only — our backend never sends the HTTP-date form. */
function retryAfterSecondsOf(res: Response): number | undefined {
  const raw = res.headers.get("Retry-After");
  // A present-but-blank header must fall through to the caller's default:
  // Number('') is 0, which would burn every retry instantly.
  if (raw === null || raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * Throw the `ApiError` for a failed response, preferring the backend's
 * `{ error }` envelope message over a generic status line. Shared by `unwrap`
 * and by the binary-body helpers (which can't go through `unwrap` at all,
 * because a success there is bytes rather than JSON) so every caller reports
 * failures identically.
 */
async function throwResponseError(res: Response): Promise<never> {
  let message = `Request failed with status ${res.status}`;
  let envelope: { error?: string } | undefined;
  try {
    const body = (await res.json()) as { error?: string } | null;
    if (body?.error) message = body.error;
    if (body && typeof body === "object") envelope = body;
  } catch {
    // ignore — non-JSON body (e.g. a gateway error page); keep generic message
  }
  const err = new ApiError(message, res.status, retryAfterSecondsOf(res)) as ApiError &
    EnvelopeError<{ error?: string }>;
  // The parsed failure body rides along as `err.envelope` — the same shape
  // `unwrapEnvelope` produces — so `vatDeclaredOf` (and App.tsx's
  // meta.expectedErrorCodes check) can read a coded 400 off ANY authed helper.
  // That makes `unwrap`/`unwrapEnvelope` redundant; they are deliberately NOT
  // folded here — that is a separate, mechanical change.
  if (envelope) err.envelope = envelope;
  throw err;
}

/**
 * Throw on non-2xx responses using the backend's `{ error }` envelope when available.
 * Returns the parsed JSON body on success.
 */
export async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) await throwResponseError(res);
  return (await res.json()) as T;
}

/** Error thrown by `unwrapEnvelope`, carrying the parsed failure envelope. */
export type EnvelopeError<T> = Error & { envelope?: T };

/**
 * Like `unwrap`, but preserves the parsed failure body on the thrown error
 * (`err.envelope`) so callers can read structured envelope fields — e.g. the
 * clearing RPCs' `code` or `allocation_index` — that a message-only Error
 * drops. Use for C3-variant routes that return the RPC envelope as the 400
 * body (see docs/BACKEND_API.md).
 */
export async function unwrapEnvelope<T extends { error?: string }>(
  res: Response,
): Promise<T> {
  if (!res.ok) {
    let envelope: T | undefined;
    try {
      envelope = (await res.json()) as T;
    } catch {
      // ignore — non-JSON body (e.g. a gateway error page); keep generic message
    }
    const err = new ApiError(
      envelope?.error || `Request failed with status ${res.status}`,
      res.status,
      retryAfterSecondsOf(res),
    ) as ApiError & EnvelopeError<T>;
    if (envelope) err.envelope = envelope;
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * The declared-VAT refusal carried by a failed mutation, or `null`.
 *
 * The api turns the `guard_declared_vat_entry` trigger's refusal into a 400
 * whose body is a {@link VatDeclaredRefusal} (`code === VAT_DECLARED`, the
 * filed period, the human text). `throwResponseError` / `unwrapEnvelope` hang
 * that body on the thrown error as `envelope`; this is the one typed reader of
 * it, so hooks do not each repeat the `(error as EnvelopeError).envelope?.code`
 * cast. Anything else — a plain Error, a non-JSON body, a different code —
 * is `null` and the caller falls back to its generic handling.
 */
export function vatDeclaredOf(error: unknown): VatDeclaredRefusal | null {
  const envelope = (error as { envelope?: { code?: string } } | null | undefined)?.envelope;
  return envelope?.code === VAT_DECLARED ? (envelope as VatDeclaredRefusal) : null;
}

/**
 * `meta` for a mutation that can be refused by the declared-VAT guard
 * (`guard_declared_vat_entry`): App.tsx's MutationCache.onError reads
 * `meta.expectedErrorCodes` against the thrown error's `envelope.code` and
 * keeps Sentry quiet for those — a filed return refusing an edit is a
 * user-facing rule, not a bug. Spread it as the mutation's `meta`; the toast
 * still fires from the hook's own onError. One definition so the eight writers
 * that carry it (expense update/delete, journal update/delete/recategorise, AR
 * invoice update, bank-feed undo/undo-split) cannot drift.
 */
export const VAT_DECLARED_MUTATION_META = {
  expectedErrorCodes: [VAT_DECLARED] as readonly string[],
} as const;

/**
 * Authed POST to a backend route that is NOT exposed via the typed Hono client —
 * e.g. routes mounted outside the `routes` chain to dodge Hono's RPC-type depth
 * limit (see apps/api/src/index.ts). Mirrors the typed client's auth: Bearer
 * token from the current Supabase session + the same relative `/api/*` base
 * (Vite-proxied in dev, vercel-rewritten in prod). Throws via `unwrap` on non-2xx.
 */
export async function authedPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  return unwrap<T>(res);
}

/**
 * Authed GET counterpart to {@link authedPost}, same auth + error semantics.
 * For backend read routes mounted outside the typed Hono client (see
 * apps/api/src/index.ts). Callers embed query params in `path`.
 */
export async function authedGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "GET",
    headers: { ...(await authHeaders()) },
  });
  return unwrap<T>(res);
}

/**
 * Authed POST that returns the raw {@link Response} (not unwrapped) so the caller
 * can read a streaming body (e.g. Server-Sent Events). Same auth as
 * {@link authedPost}; the caller is responsible for checking `res.ok` and
 * consuming `res.body`.
 */
export async function authedStream(path: string, body: unknown): Promise<Response> {
  return fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
}

/** Authed PUT counterpart to {@link authedPost}, same auth + error semantics. */
export async function authedPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  return unwrap<T>(res);
}

/** Authed PATCH counterpart to {@link authedPost}, same auth + error semantics. */
export async function authedPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  return unwrap<T>(res);
}

/** Authed DELETE counterpart to {@link authedPost}, same auth + error semantics. */
export async function authedDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "DELETE",
    headers: { ...(await authHeaders()) },
  });
  return unwrap<T>(res);
}

/**
 * UNAUTHENTICATED GET to a public backend route (`/api/v1/public/*`, e.g. the
 * survey answer page `/s/:token`). Deliberately sends NO Authorization header
 * — even when a session exists in this browser — so a public page never
 * carries a staff identity to an anonymous endpoint. Same error semantics as
 * {@link authedGet} (`ApiError` with `status` + `envelope`).
 */
export async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "GET",
    credentials: "omit",
  });
  return unwrap<T>(res);
}

/** Unauthenticated POST counterpart to {@link publicGet} (no Authorization header, no cookies). */
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(body),
  });
  return unwrap<T>(res);
}

/**
 * The download name a response asks for, read from `Content-Disposition`.
 *
 * The backend sends the name twice (see `contentDispositionValue` in
 * `@releve/documents/filenames`): an ASCII-only `filename="…"` every client
 * understands, and the RFC 5987 `filename*=UTF-8''…` form carrying the real,
 * possibly non-ASCII name. The extended form wins when it is present and
 * decodable — a malformed percent-encoding falls back to the ASCII spelling
 * rather than throwing mid-download.
 *
 * Returns `undefined` when the header is missing or carries no filename, which
 * is the caller's cue to use its own fallback.
 */
function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;

  const extended = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch {
      // Malformed %-escape — fall through to the ASCII parameter.
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  return bare?.[1].trim() || undefined;
}

/**
 * Authed POST whose response is a FILE, saved to the user's downloads.
 *
 * Same auth and failure semantics as {@link authedPost} — a non-2xx response
 * throws the `ApiError` carrying the backend's `{ error }` envelope message —
 * but a success is bytes, not JSON: the body becomes an object URL that a
 * synthetic anchor click hands to the browser (the repo's download convention,
 * per `WkrReportTab`). The name comes from the response's `Content-Disposition`
 * when it has one, so the server stays the authority on what a document is
 * called; `fallbackFilename` covers a proxy that strips the header. A 200 whose
 * `Content-Type` is not `application/pdf` throws rather than being saved.
 *
 * Used by the PDF endpoints (`POST /api/v1/pdf/orgs/:orgId/{invoice,report}`),
 * which are mounted outside the typed Hono client.
 */
export async function authedPdf(
  path: string,
  body: unknown,
  fallbackFilename: string,
): Promise<void> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/pdf",
      ...(await authHeaders()),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) await throwResponseError(res);

  // A 200 that is not a PDF is a proxy or gateway answering in the backend's
  // place (an SSO interstitial, an HTML error page) — saving it would hand the
  // user a `.pdf` that no reader can open, with no clue why. Fail like any
  // other bad response instead.
  const contentType = res.headers.get("Content-Type") ?? "";
  if (!contentType.toLowerCase().startsWith("application/pdf")) {
    throw new ApiError(
      `Expected a PDF but the server returned ${contentType || "no content type"}`,
      res.status,
    );
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  // Scheduled before the click, so the URL is released even if the click path
  // throws — deferred rather than `finally` because the click only STARTS the
  // download: revoking in the same tick can pull the blob out from under a
  // browser that has not read it yet (the empty-download bug). Same 1s grace
  // the file preview path uses (`components/ui/file-attachments.tsx`).
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download =
    filenameFromContentDisposition(res.headers.get("Content-Disposition")) ?? fallbackFilename;
  // Repo download convention (ImportExportTools, WkrReportTab): append before click.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
